import Deferred from 'deferred.js';
import http from 'http.js';
import ko from 'knockout';

// Load data from an API url, use it to instantiate models, and load them
// into desired collection, handling dependencies properly.
export function loadData(
    url,
    queryData,
    collection,
    model,
    manager,
    deferred,
    dependsOn,
    sortField,
) {
    if (typeof queryData !== 'object') {
        throw new Error(`queryData must be object, is ${queryData}`);
    }
    if (typeof model === 'undefined') {
        throw new Error('Model argument is required.');
    }
    if (typeof dependsOn === 'undefined') {
        dependsOn = [];
    }
    if (!deferred) {
        deferred = new Deferred();
    }
    let allData = [];
    function load(args) {
        http.get({url, data: args}).then((data) => {
            const next = data.next;
            if (data.results) {
                data = data.results;
            }
            allData = allData.concat(data);
            if (next) {
                if (!args.page) {
                    args.page = 1;
                }
                args.page++;
                load(args);
            } else {
                if (sortField) {
                    allData.sort((a, b) => {
                        if (a[sortField] < b[sortField]) {
                            return -1;
                        } else if (a[sortField] === b[sortField]) {
                            return 0;
                        } else {
                            return 1;
                        }
                    });
                }
                Promise.all(dependsOn).then(() => {
                    const instances = [];
                    for (const instanceData of allData) {
                        // Allow model to be a function that selects what
                        // model to use based on the data
                        let InstanceModel;
                        if (model.selectsModel) {
                            InstanceModel = model(instanceData);
                        } else {
                            InstanceModel = model;
                        }
                        // If selector function returns nothing, skip this
                        if (InstanceModel) {
                            const instance = new InstanceModel(manager);
                            instance.load(instanceData);
                            instances.push(instance);
                        }
                    }
                    collection(instances);
                    deferred.resolve();
                }, deferred.reject);
            }
        }, deferred.reject);
    }
    load(queryData);
    return deferred;
}

// Monitor changes to an observable array. Returns two observable arrays that
// correspond to the added and removed items in this list.
// Also returns clear function, if you have made changes to these arrays to not
// count as changes, e.g. on initial load
// TODO when needed -- take a list of observable names to monitor on individual
// objects. If any of them change, put them on a changed list that also gets
// returned -- this allows us to neatly map these lists to items to be
// PUTed DELETEd and POSTed
export function monitorArray(array) {
    const added = ko.observableArray();
    const removed = ko.observableArray();
    array.subscribe(
        function(changes) {
            for (const change of changes) {
                let idx;
                // If item moved, nothing we care to track
                if (typeof change.moved !== 'undefined') {
                    return; // continue
                }
                if (change.status === 'added') {
                    // If adding something removed, just don't remove anymore
                    idx = removed.indexOf(change.value);
                    if (idx === -1) {
                        added.push(change.value);
                    } else {
                        removed.splice(idx, 1);
                    }
                } else if (change.status === 'deleted') {
                    // If removing something just added, just don't add anymore
                    idx = added.indexOf(change.value);
                    if (idx === -1) {
                        removed.push(change.value);
                    } else {
                        added.splice(idx, 1);
                    }
                }
            }
        },
        null,
        'arrayChange',
    );
    function clear() {
        added([]);
        removed([]);
    }
    // This method doesn't retain order
    function revert() {
        const toAdd = removed();
        const toRemove = added();
        for (const obj of toAdd) {
            array().push(obj);
        }
        for (const obj of toRemove) {
            const index = array.indexOf(obj);
            array().splice(index, 1);
        }
        clear();
    }
    return {
        added,
        removed,
        revert,
        clear,
    };
}

// Truncate an observable to a given length.
// truncation gets appended to the end if the size of the observable
// content is less than maxLength.
// obs can have HTML content.
export function truncateHtml(obs, truncation, maxLength) {
    return ko.pureComputed(function() {
        if (!obs()) {
            return obs();
        }
        const tmp = document.createElement('div');
        tmp.innerHTML = obs();
        const child = tmp.firstChild;
        const startStripped =
            child.innerText || child.textContent || child.data || '';
        const allStripped = tmp.innerText || tmp.textContent;
        if (allStripped.length < maxLength) {
            return obs();
        } else {
            return startStripped.substr(0, maxLength) + truncation();
        }
    });
}
